/**
 * Jobs Admin Page
 *
 * Real-time job monitoring with filtering and admin actions
 */
import { useMemo, useState } from "react";
import { useSnapshot } from "valtio";
import { Play, Clock, CheckCircle, XCircle, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import {
  JobStatusBadge,
  getStatusMessage,
} from "@/features/admin/components/JobStatusBadge";
import { JobDetailModal } from "./JobDetailModal";
import {
  jobsStore,
  setFilter,
  setSelectedJob,
  matchesFilter,
  deleteJobs,
  type JobFilter,
} from "@/features/admin/stores/jobs";
import { useProcessingJobs, type JobStatus } from "@/lib/instantdb";

// Type for job with project relation from InstantDB
interface JobWithProject {
  id: string;
  projectId: string;
  status: string;
  progress: number;
  message: string;
  startedAt: number;
  completedAt?: number;
  outputR2Key?: string;
  error?: string;
  analyticsJson?: Record<string, unknown>;
  edlJson?: Record<string, unknown>;
  project?: Array<{
    id: string;
    userId: string;
    name: string;
    style: string;
    duration: number;
  }>;
}

const filterTabs: { key: JobFilter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "queued", label: "Queued" },
  { key: "active", label: "Active" },
  { key: "completed", label: "Completed" },
  { key: "failed", label: "Failed" },
];

export default function Jobs() {
  const { filter, selectedJobId, actionLoading } = useSnapshot(jobsStore);
  const { data, isLoading, error } = useProcessingJobs(200);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const jobs = (data?.processing_jobs ?? []) as JobWithProject[];

  // Compute counts for each filter
  const counts = useMemo(() => {
    const now = Date.now();
    const oneDayAgo = now - 24 * 60 * 60 * 1000;

    return {
      all: jobs.length,
      queued: jobs.filter((j) => j.status === "queued").length,
      active: jobs.filter(
        (j) =>
          j.status === "uploading" ||
          j.status === "analyzing" ||
          j.status === "rendering",
      ).length,
      completed: jobs.filter(
        (j) =>
          j.status === "completed" &&
          j.completedAt &&
          j.completedAt > oneDayAgo,
      ).length,
      failed: jobs.filter(
        (j) =>
          j.status === "failed" && j.completedAt && j.completedAt > oneDayAgo,
      ).length,
    };
  }, [jobs]);

  // Filter jobs based on current filter
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) =>
      matchesFilter(job.status as JobStatus, filter),
    );
  }, [jobs, filter]);

  const selectedJob = jobs.find((j) => j.id === selectedJobId);

  // Get deletable jobs based on current filter (all filters except "all" can bulk delete)
  const deletableJobs = useMemo(() => {
    if (filter === "all") return [];
    return filteredJobs;
  }, [filteredJobs, filter]);

  // Get label for bulk delete button
  const getFilterLabel = (f: JobFilter): string => {
    switch (f) {
      case "queued": return "Queued";
      case "active": return "Active";
      case "completed": return "Completed";
      case "failed": return "Failed";
      default: return "";
    }
  };

  const handleBulkDelete = async () => {
    if (deletableJobs.length === 0) return;

    try {
      await deleteJobs(deletableJobs.map((j) => j.id));
      toast.success(`Deleted ${deletableJobs.length} jobs`);
      setShowDeleteConfirm(false);
    } catch {
      toast.error("Failed to delete jobs");
    }
  };

  return (
    <div className="flex h-full flex-col">
      {/* Header */}
      <header className="shrink-0 border-b bg-background px-4 py-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-3">
            <Play className="h-8 w-8 text-violet-600" />
            <div>
              <h1 className="text-3xl font-bold">Jobs</h1>
              <p className="mt-1 text-muted-foreground">
                Monitor render jobs in real-time
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          {/* Stats cards */}
          <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <StatsCard
              icon={<Clock className="h-5 w-5 text-slate-500" />}
              label="Queued"
              value={counts.queued}
              loading={isLoading}
            />
            <StatsCard
              icon={<Loader2 className="h-5 w-5 animate-spin text-violet-500" />}
              label="Active"
              value={counts.active}
              loading={isLoading}
            />
            <StatsCard
              icon={<CheckCircle className="h-5 w-5 text-emerald-500" />}
              label="Completed (24h)"
              value={counts.completed}
              loading={isLoading}
            />
            <StatsCard
              icon={<XCircle className="h-5 w-5 text-red-500" />}
              label="Failed (24h)"
              value={counts.failed}
              loading={isLoading}
            />
          </div>

          {/* Filter tabs and bulk actions */}
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {filterTabs.map((tab) => (
                <Button
                  key={tab.key}
                  variant={filter === tab.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFilter(tab.key)}
                >
                  {tab.label}
                  <Badge variant="secondary" className="ml-2">
                    {counts[tab.key]}
                  </Badge>
                </Button>
              ))}
            </div>

            {/* Bulk delete button */}
            {filter !== "all" && deletableJobs.length > 0 && (
              <div className="flex items-center gap-2">
                {showDeleteConfirm ? (
                  <>
                    <span className="text-sm text-muted-foreground">
                      Delete {deletableJobs.length} {filter} jobs?
                    </span>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDelete}
                      disabled={actionLoading}
                    >
                      {actionLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="mr-2 h-4 w-4" />
                      )}
                      Confirm
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDeleteConfirm(false)}
                      disabled={actionLoading}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete All {getFilterLabel(filter)}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Error state */}
          {error && (
            <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
              Failed to load jobs: {error.message}
            </div>
          )}

          {/* Job list */}
          {isLoading ? (
            <div className="grid gap-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-24" />
              ))}
            </div>
          ) : (
            <div className="grid gap-4">
              {filteredJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  onClick={() => setSelectedJob(job.id)}
                />
              ))}

              {filteredJobs.length === 0 && (
                <div className="py-12 text-center text-muted-foreground">
                  No jobs found
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Detail modal */}
      {selectedJob && (
        <JobDetailModal
          job={selectedJob}
          onClose={() => setSelectedJob(null)}
        />
      )}
    </div>
  );
}

// ========== Sub-components ==========

interface StatsCardProps {
  icon: React.ReactNode;
  label: string;
  value: number;
  loading?: boolean;
}

function StatsCard({ icon, label, value, loading }: StatsCardProps) {
  return (
    <Card className="flex items-center gap-3 p-4">
      {icon}
      <div>
        {loading ? (
          <Skeleton className="mb-1 h-7 w-12" />
        ) : (
          <p className="text-2xl font-bold">{value}</p>
        )}
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </Card>
  );
}

interface JobCardProps {
  job: JobWithProject;
  onClick: () => void;
}

function JobCard({ job, onClick }: JobCardProps) {
  const project = job.project?.[0];
  const isActive =
    job.status === "uploading" ||
    job.status === "analyzing" ||
    job.status === "rendering";

  // Format time ago
  const formatTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  return (
    <Card
      className="cursor-pointer p-4 transition-colors hover:bg-muted/50"
      onClick={onClick}
    >
      <div className="flex items-start gap-4">
        {/* Progress indicator */}
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-muted">
          {isActive ? (
            <div className="text-center">
              <span className="text-lg font-bold text-violet-600">
                {job.progress}%
              </span>
            </div>
          ) : job.status === "completed" ? (
            <CheckCircle className="h-6 w-6 text-emerald-500" />
          ) : job.status === "failed" ? (
            <XCircle className="h-6 w-6 text-red-500" />
          ) : (
            <Clock className="h-6 w-6 text-slate-400" />
          )}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center gap-2">
            <JobStatusBadge status={job.status as JobStatus} />
            {project && (
              <span className="text-sm text-muted-foreground">
                {project.style}/{project.duration}s
              </span>
            )}
          </div>

          {/* Progress bar for active jobs */}
          {isActive && (
            <Progress value={job.progress} className="mb-2 h-2" />
          )}

          <p className="text-sm text-muted-foreground">
            {job.message || getStatusMessage(job.status as JobStatus)}
          </p>

          <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
            {project && <span>{project.name}</span>}
            <span>Started {formatTimeAgo(job.startedAt)}</span>
            {job.completedAt && (
              <span>Finished {formatTimeAgo(job.completedAt)}</span>
            )}
          </div>

          {/* Error message preview */}
          {job.error && (
            <p className="mt-2 truncate text-sm text-red-600">{job.error}</p>
          )}
        </div>
      </div>
    </Card>
  );
}
